import { Command } from 'commander';
import { ApiClient } from '../client.js';
import { formatOutput } from '../output.js';
import { parseData, getDatabase, getFormat } from '../helpers.js';
import { confirm } from '../prompt.js';

export const relationshipTypesCommand = new Command('relationship-types')
  .description('Manage relationship types (the schema for a relationship\'s metadata)');

relationshipTypesCommand.command('list')
  .description('List all relationship types in a database')
  .action(async function (this: Command) {
    const ws = getDatabase(this);
    const client = new ApiClient();
    const data = await client.request('GET', `/v1/${ws}/relationship-types`);
    console.log(formatOutput(data, getFormat(this)));
  });

relationshipTypesCommand.command('get <rel_type>')
  .description('Get a relationship type')
  .action(async function (this: Command, relType: string) {
    const ws = getDatabase(this);
    const client = new ApiClient();
    const data = await client.request('GET', `/v1/${ws}/relationship-types/${relType}`);
    console.log(formatOutput(data, getFormat(this)));
  });

relationshipTypesCommand.command('upsert <rel_type>')
  .description('Create or update a relationship type')
  .option('--description <desc>', 'Description')
  .option('--schema <json>', 'JSON Schema for the relationship data (inline JSON or @filename). Omit to allow any data.')
  .option('--source-collections <json>', 'JSON array of allowed source collections (inline JSON or @filename)')
  .option('--target-collections <json>', 'JSON array of allowed target collections (inline JSON or @filename)')
  .action(async function (this: Command, relType: string, opts: { description?: string; schema?: string; sourceCollections?: string; targetCollections?: string }) {
    const ws = getDatabase(this);
    const client = new ApiClient();
    const body: Record<string, unknown> = {};
    if (opts.description) body['description'] = opts.description;
    if (opts.schema) body['data_schema'] = parseData(opts.schema);
    if (opts.sourceCollections) body['source_collections'] = parseData(opts.sourceCollections);
    if (opts.targetCollections) body['target_collections'] = parseData(opts.targetCollections);
    const data = await client.request('PUT', `/v1/${ws}/relationship-types/${relType}`, body);
    console.log(formatOutput(data, getFormat(this)));
  });

relationshipTypesCommand.command('delete <rel_type>')
  .description('Delete a relationship type')
  .option('-y, --yes', 'Skip confirmation prompt')
  .action(async function (this: Command, relType: string, opts: { yes?: boolean }) {
    if (!opts.yes && !(await confirm(`Delete relationship type "${relType}"? This cannot be undone.`))) {
      console.log('Aborted');
      return;
    }
    const ws = getDatabase(this);
    const client = new ApiClient();
    await client.request('DELETE', `/v1/${ws}/relationship-types/${relType}`);
    console.log('Deleted');
  });

relationshipTypesCommand.command('history <id>')
  .description('View the change history (audit log) for a relationship type — every config version, who changed it, and when')
  .option('--limit <n>', 'Max results')
  .option('--offset <n>', 'Pagination offset')
  .option('--diff', 'Include field-level diffs between versions')
  .action(async function (this: Command, id: string, opts: { limit?: string; offset?: string; diff?: boolean }) {
    const ws = getDatabase(this);
    const client = new ApiClient();
    const qs = new URLSearchParams();
    if (opts.limit) qs.set('limit', opts.limit);
    if (opts.offset) qs.set('offset', opts.offset);
    if (opts.diff) qs.set('diff', 'true');
    const q = qs.toString();
    const data = await client.request('GET', `/v1/${ws}/audit/relationship-types/${id}${q ? `?${q}` : ''}`);
    console.log(formatOutput(data, getFormat(this)));
  });
